import { memo, useRef, useEffect, useState, useCallback } from "react";
import { parseISO, startOfDay, endOfDay, getTime } from "date-fns";
import type { TimeEntry } from "@/types/time";
import { formatDuration, formatTime12h } from "@/lib/time-utils";

interface DayTimelineProps {
  entries: TimeEntry[];
  date: string;
  projectColors: Map<string, string>;
}

interface Block {
  x: number;
  width: number;
  color: string;
  entry: TimeEntry;
}

const HOUR_LABELS = [
  "AM", "2 AM", "4 AM", "6 AM", "8 AM", "10 AM",
  "12 PM", "2 PM", "4 PM", "6 PM", "8 PM", "10 PM",
  "AM",
];
const BAR_HEIGHT = 24;
const LABEL_HEIGHT = 20;
const CANVAS_HEIGHT = BAR_HEIGHT + LABEL_HEIGHT + 8;
const DEFAULT_COLOR = "#94A3B8";

export const DayTimeline = memo(function DayTimeline({ entries, date, projectColors }: DayTimelineProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasWidth, setCanvasWidth] = useState(800);
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    entry: TimeEntry;
  } | null>(null);
  const blocksRef = useRef<Block[]>([]);

  const dateObj = parseISO(date);
  const dayStart = getTime(startOfDay(dateObj));
  const dayEnd = getTime(endOfDay(dateObj));
  const dayMs = dayEnd - dayStart;

  const buildBlocks = useCallback(
    (width: number): Block[] => {
      return entries.map((entry) => {
        const start = Math.max(getTime(parseISO(entry.start)), dayStart);
        const end = Math.min(getTime(parseISO(entry.end)), dayEnd);
        const x = ((start - dayStart) / dayMs) * width;
        const blockWidth = Math.max(((end - start) / dayMs) * width, 2);
        const color = entry.projectId
          ? projectColors.get(entry.projectId) ?? DEFAULT_COLOR
          : DEFAULT_COLOR;
        return { x, width: blockWidth, color, entry };
      });
    },
    [entries, dayStart, dayEnd, dayMs, projectColors]
  );

  const draw = useCallback(
    (ctx: CanvasRenderingContext2D, width: number) => {
      const dpr = window.devicePixelRatio || 1;
      ctx.clearRect(0, 0, width * dpr, CANVAS_HEIGHT * dpr);
      ctx.scale(dpr, dpr);

      // Background bar
      ctx.fillStyle = "rgba(128, 128, 128, 0.08)";
      ctx.beginPath();
      ctx.roundRect(0, 0, width, BAR_HEIGHT, 4);
      ctx.fill();

      // Grid lines and labels
      ctx.fillStyle = "rgba(128, 128, 128, 0.3)";
      ctx.font = "10px system-ui, sans-serif";
      ctx.textAlign = "center";
      for (let i = 0; i <= 12; i++) {
        const x = (i * 2 / 24) * width;
        const tickX = i === 12 ? x - 1 : x;
        ctx.fillRect(tickX, BAR_HEIGHT + 2, 1, 4);
        ctx.textAlign = i === 0 ? "left" : i === 12 ? "right" : "center";
        ctx.fillText(HOUR_LABELS[i], x, BAR_HEIGHT + LABEL_HEIGHT + 4);
      }
      ctx.textAlign = "center";

      // Entry blocks
      const blocks = buildBlocks(width);
      blocksRef.current = blocks;
      for (const block of blocks) {
        ctx.fillStyle = block.color;
        ctx.beginPath();
        ctx.roundRect(block.x, 2, block.width, BAR_HEIGHT - 4, 3);
        ctx.fill();
      }

      ctx.setTransform(1, 0, 0, 1, 0, 0);
    },
    [buildBlocks]
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      setCanvasWidth(width);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvasWidth * dpr;
    canvas.height = CANVAS_HEIGHT * dpr;
    canvas.style.width = `${canvasWidth}px`;
    canvas.style.height = `${CANVAS_HEIGHT}px`;

    draw(ctx, canvasWidth);
  }, [canvasWidth, draw]);

  function handleMouseMove(e: React.MouseEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (y > BAR_HEIGHT) {
      setTooltip(null);
      return;
    }

    const hit = blocksRef.current.find(
      (b) => x >= b.x && x <= b.x + b.width
    );

    if (hit) {
      setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, entry: hit.entry });
    } else {
      setTooltip(null);
    }
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <canvas
        ref={canvasRef}
        className="w-full cursor-crosshair"
        style={{ height: CANVAS_HEIGHT }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setTooltip(null)}
      />
      {tooltip && (
        <div
          className="pointer-events-none absolute z-50 rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md"
          style={{
            left: Math.min(tooltip.x, canvasWidth - 180),
            top: tooltip.y - (tooltip.entry.project || tooltip.entry.task ? 78 : 60),
          }}
        >
          <p className="text-sm font-medium">{formatDuration(tooltip.entry.durationSeconds)}</p>
          {(tooltip.entry.project || tooltip.entry.task) && (
            <p className="text-muted-foreground">
              {tooltip.entry.project?.name}
              {tooltip.entry.task && ` · ${tooltip.entry.task.name}`}
            </p>
          )}
          <p className="text-muted-foreground">
            {tooltip.entry.description ?? "No description"}
          </p>
          <p className="text-muted-foreground">
            {formatTime12h(tooltip.entry.start)} – {formatTime12h(tooltip.entry.end)}
          </p>
        </div>
      )}
    </div>
  );
});
